import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Badge from "@mui/material/Badge";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Services
import licenciaService from "services/licenciaService";

/**
 * Componente visual que muestra el contador de licencias disponibles
 * Se actualiza automáticamente cuando cambian las licencias
 */
function LicenseCounter({ iconColor = "inherit", showLabel = false }) {
  const [licencias, setLicencias] = useState({
    licencias_disponibles: 0,
    licencias_compradas: 0,
    licencias_usadas: 0
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Cargar licencias iniciales
  useEffect(() => {
    cargarLicencias();

    // Escuchar evento de actualización de licencias
    const handleLicenciasActualizadas = (event) => {
      setLicencias(event.detail);
    };

    window.addEventListener('licenciasActualizadas', handleLicenciasActualizadas);

    return () => {
      window.removeEventListener('licenciasActualizadas', handleLicenciasActualizadas);
    };
  }, []);

  const cargarLicencias = async () => {
    try {
      setLoading(true);
      
      // Primero intentar desde localStorage para carga rápida
      const licenciasLocal = licenciaService.getLicenciasFromLocalStorage();
      if (licenciasLocal) {
        setLicencias(licenciasLocal);
      }
      
      // Luego actualizar desde el servidor
      const licenciasServer = await licenciaService.getLicencias();
      setLicencias(licenciasServer);
      
      // Actualizar localStorage con los valores del servidor
      licenciaService.actualizarLicenciasEnLocalStorage(licenciasServer);
    } catch (error) {
      console.error('Error al cargar licencias:', error);
      // Si hay error, intentar usar valores del localStorage
      const licenciasLocal = licenciaService.getLicenciasFromLocalStorage();
      if (licenciasLocal) {
        setLicencias(licenciasLocal);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    navigate('/gestion-licencias/comprar');
  };

  // Determinar el color según la cantidad de licencias disponibles
  const getColor = () => {
    const disponibles = licencias.licencias_disponibles || 0;
    if (disponibles === 0) return 'error';
    if (disponibles < 10) return 'warning';
    return 'success';
  };

  // Calcular el porcentaje de uso
  const getUsagePercentage = () => {
    if (licencias.licencias_compradas === 0) return 0;
    return Math.round((licencias.licencias_usadas / licencias.licencias_compradas) * 100);
  };

  const tooltipContent = (
  <MDBox p={1}>
    <MDTypography variant="caption" color="white" fontWeight="bold" display="block">
      Estado de Licencias
    </MDTypography>
    <MDTypography variant="caption" color="white" display="block" mt={1}>
      Totales: {licencias.licencias_compradas}
    </MDTypography>
    <MDTypography variant="caption" color="white" display="block">
      Usadas: {licencias.licencias_usadas}
    </MDTypography>
    <MDTypography variant="caption" color="white" display="block">
      Disponibles: {licencias.licencias_disponibles || 0}
    </MDTypography>
    <MDTypography variant="caption" color="white" display="block" mt={1}>
      Uso: {getUsagePercentage()}%
    </MDTypography>
    <MDTypography 
      variant="caption" 
      color="white" 
      display="block" 
      mt={1} 
      onClick={handleClick}
      sx={{ 
        fontStyle: 'italic',
        fontWeight: 'bold',
        cursor: 'pointer',
        '&:hover': {
          textDecoration: 'underline',
          color: '#90caf9'
        }
      }}
    >
      Click para comprar más licencias
    </MDTypography>
  </MDBox>
  );
  if (loading) {
    return (
      <MDBox display="flex" alignItems="center" sx={{ cursor: 'pointer' }} onClick={handleClick}>
        <Icon sx={{ color: iconColor }}>hourglass_empty</Icon>
      </MDBox>
    );
  }

  return (
    <Tooltip 
      title={tooltipContent} 
      placement="bottom" 
      arrow
      disableInteractive={false}
      PopperProps={{
      sx: {
      pointerEvents: 'auto',
      }
    }}
    >
      <MDBox 
        display="flex" 
        alignItems="center" 
        gap={1}
        sx={{ 
            transform: 'scale(1.05)'
          }
        }
      >
        <Badge 
          badgeContent={licencias.licencias_disponibles || 0}
          color={getColor()}
          max={999}
        >
          <Icon sx={{ color: iconColor }}>card_membership</Icon>
        </Badge>
        
        {showLabel && (
          <MDTypography 
            variant="button" 
            fontWeight="medium" 
            color={iconColor}
          >
            {licencias.licencias_disponibles || 0} Licencias
          </MDTypography>
        )}
      </MDBox>
    </Tooltip>
  );
}

LicenseCounter.propTypes = {
  iconColor: PropTypes.string,
  showLabel: PropTypes.bool
};

export default LicenseCounter;